import { NavigateService } from '@shared/services/navigate.service';
import { StorageService } from '@core/services/storage.service';
import { TaskType } from '@shared/enums/task-type.enum';
import { catchError, filter, tap } from 'rxjs/operators';
import { EMPTY, Subscription } from 'rxjs';
import { TaskSidenavService } from '@shared/modules/task-sidenav/services/task-sidenav.service';
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { TaskSidenavManager } from '@shared/modules/task-sidenav/managers/task-sidenav.manager';
import { PermissionsGroups } from '@core/permissions/permissions.group';
import { Task } from '@shared/models/task.model';
import { Config } from '@shared/configs/config';
import {
  ListTaskEvent,
  ListTaskEventType,
  ListTaskService
} from '@shared/modules/list/services/list-task.service';
import { TaskController } from '@shared/controllers/task.controller';
import { TASK_SIDENAV_FORM } from '@shared/modules/task-sidenav/const/task-sidenav-form';
import { TaskPermissionController } from '@shared/controllers/task-permission.controller';
import { Project } from '@modules/projects/shared/models/project.model';
import { SnackBarService } from '@core/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { ChangeType } from '@shared/modules/task-sidenav/enums/change-type.enum';
import { DomSanitizer } from '@angular/platform-browser';
import { CheckPermission } from '@core/permissions/check-permission';
import { merge, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { TaskService } from '@shared/modules/tasks/services/task-service';
import { Validators, UntypedFormControl } from '@angular/forms';

@Component({
  selector: 'task-basic-preview-data',
  templateUrl: './task-basic-preview-data.component.html',
  styleUrls: ['./task-basic-preview-data.component.scss']
})
export class TaskBasicPreviewDataComponent implements OnInit, OnDestroy {
  PermissionsGroups = PermissionsGroups;
  Config = Config;
  TaskType = TaskType;

  sub: Subscription = new Subscription();
  taskManager: TaskSidenavManager = new TaskSidenavManager();
  taskPermission: TaskPermissionController = new TaskPermissionController();
  canProjectAdd = true;
  isProjectRequired = false;
  private readonly refreshGroups$ = new Subject<void>();
  private projectIdCtrl = this.service.form.get(TASK_SIDENAV_FORM.projectId);
  protected titleEditControl = new UntypedFormControl('', [Validators.required]);
  protected contentEditControl = new UntypedFormControl(null);
  protected editingTitle = false;
  protected editingDescription = false;

  get shouldShowProjectInfo(): boolean {
    return (
      !!this.service?.task?.assignedTo?.[0]?.userPerson?.previewName ||
      !!this.service?.task?.assignedEmails?.length ||
      !!this.service?.task?.project?.fullAddress
    );
  }

  get assignedDisplayName(): string {
    return (
      this.service?.task?.assignedTo?.[0]?.userPerson?.previewName ||
      this.service?.task?.assignedEmails?.[0] ||
      ''
    );
  }

  get isProcessTaskContext(): boolean {
    return (
      !!this.service?.isProcessTaskContext ||
      this.service?.groupType === 'activity-template' ||
      !!this.service?.activityTemplateContext
    );
  }

  get allowInlineEditing(): boolean {
    return !this.service?.task?.isDeleted && !!this.taskPermission?.edit;
  }

  get shouldShowInlineDescriptionPlaceholder(): boolean {
    return !this.editingDescription && !this.service?.safeContent;
  }

  get shouldShowDescriptionValue(): boolean {
    return !this.editingDescription && !!this.service?.safeContent;
  }

  get titlePlaceholderKey(): string {
    return this.isProcessTaskContext
      ? 'Projects.processTaskPlaceholders.title'
      : 'ProjectContacts.List.name';
  }

  get descriptionPlaceholderKey(): string {
    if (!this.isProcessTaskContext) {
      return `Projects.enterDescription-${this.service.FormType}`;
    }

    const type = this.service?.task?.type || this.service?.activityTemplateTaskType || 'task';
    return `Projects.processTaskPlaceholders.description.${type}`;
  }

  taskGroups$ = (this.listTaskService.isMyWorkTaskLists
    ? this.projectIdCtrl.valueChanges.pipe(
        startWith(this.projectIdCtrl.value),
        distinctUntilChanged(),
        tap((pid) => {
          if (!this.projectIdCtrl.pristine && this.service.groupType !== 'project') {
            this.resetProjectControl();
          }
          this.listTaskService.projectId = pid;
        }),
        switchMap((pid) => {
          const groupType = pid || this.service.groupType === 'project' ? 'project' : 'without_project';
          return this.taskService.getWorkspaceTaskGroupByType(groupType);
        })
      )
    : merge(
        this.refreshGroups$,
        this.projectIdCtrl.valueChanges.pipe(
          startWith(this.projectIdCtrl.value),
          distinctUntilChanged(),
          tap((pid) => (this.service.projectID = pid))
        )
      ).pipe(
        switchMap(() => {
          const formTypeValue = this.service.form.get(this.service.TASK_SIDENAV_FORM.type)!.value;
          const currentProjectId = this.projectIdCtrl.value;

          if (formTypeValue === 'change') return of([]);
          return this.taskService.getTaskGroupByType(formTypeValue, currentProjectId);
        })
      )
  ).pipe(
    map((groups) =>
      ((groups ?? []) as []).map(({ id, name }) => ({
        id,
        name: name === 'general' ? this.t.instant('ProjectTasks.TaskGroup.general') : name
      }))
    )
  );

  protected readonly changeTypes = Object.values(ChangeType).map((key) => ({
    id: key as ChangeType,
    name: this.t.instant(`Projects.changeType.${key}`)
  })) as Array<{ id: ChangeType; name: string }>;

  constructor(
    public storage: StorageService,
    public service: TaskSidenavService,
    private changes: ChangeDetectorRef,
    private listTaskService: ListTaskService,
    private navigateService: NavigateService,
    private snackBarService: SnackBarService,
    public t: TranslateService,
    private sanitizer: DomSanitizer,
    private taskService: TaskService
  ) {}

  ngOnInit() {
    this.listenListTaskEvents();
    this.subscribeTaskUpdate();
    this.initPermissions();
    this.subscribeProjectUpdate();
    this.syncInlineEditors();
  }

  initPermissions() {
    this.taskPermission.initPermissions(this.service.task);
    const ctrl = new CheckPermission({
      group: PermissionsGroups.PROJECTS,
      action: 'ADD',
      objectCreatorId: []
    });
    this.canProjectAdd = ctrl.check();

    if (this.listTaskService.isMyWorkTaskLists) {
      if (this.service.groupType === 'project') {
        this.isProjectRequired = true;
      }
    }
    this.changes.detectChanges();
  }

  listenListTaskEvents() {
    const actions = [ListTaskEventType.UPDATE_TASK];
    const sub = this.listTaskService.emitter
      .pipe(filter((i) => actions.indexOf(i.type) > -1))
      .subscribe(this.updateTask.bind(this));
    this.sub.add(sub);
  }

  updateTaskFromCheckbox({ task, subtasksCompleted }: { task: Task; subtasksCompleted?: boolean }) {
    const ctrl = new TaskController(task);
    ctrl
      .changeTaskStatus(task, this.listTaskService.isMyWorkTaskLists && subtasksCompleted === false)
      .pipe(
        tap(() => {
          this.snackBarService.success(this.getStatusChangedMessage(task));
          this.listTaskService.emitter.emit({
            type: ListTaskEventType.UPDATE_COUNTERS,
            data: { projectId: task?.project?.id, groupType: task.type }
          });

          this.listTaskService.emitter.emit({ type: ListTaskEventType.UPDATE_TASK_LIST });
          this.changes.detectChanges();
        }),
        catchError(() => {
          this.snackBarService.error(this.t.instant('Tasks.errorStatusChange'));
          return EMPTY;
        })
      )
      .subscribe();
  }

  private getStatusChangedMessage(task: Task): string {
    const taskType = task?.type === 'company-task' || task?.type === 'client-task' ? 'task' : task?.type || 'task';
    const actionKey = task?.status === 'completed' ? 'completed' : 'created';

    return this.t.instant('Tasks.successStatusChangeAction', {
      taskType: this.t.instant(`Tasks.TaskType.${taskType}`),
      action: this.t.instant(`Tasks.statusAction.${actionKey}`)
    });
  }

  updateTask(e: ListTaskEvent) {
    if (e.data.id === this.service.task.id) {
      this.service.initPreviewState(e.data);
      this.service.form.get(TASK_SIDENAV_FORM.status).patchValue(e.data.status, { emitEvent: false });
      this.changes.detectChanges();
    }
  }

  onStatusChange(task: Task) {
    this.service.setStatusCompleted(task);
  }

  checkedSubtasks(checked: boolean) {}

  subscribeProjectUpdate() {
    const sub = this.service.form
      .get(TASK_SIDENAV_FORM.projectId)
      .valueChanges.pipe(
        distinctUntilChanged(),
        debounceTime(0)
      )
      .subscribe(() => this.refreshGroups$.next());
    this.sub.add(sub);
  }

  resetProjectControl(): void {
    if (this.listTaskService.isMyWorkTaskLists) {
      this.service.form.get(this.service.TASK_SIDENAV_FORM.groupId).reset(null);
    }
  }

  protected startTitleEdit(): void {
    if (!this.allowInlineEditing) return;
    this.editingTitle = true;
    this.titleEditControl.setValue(this.service?.task?.description || '');
    this.titleEditControl.markAsPristine();
  }

  protected cancelTitleEdit(): void {
    this.editingTitle = false;
    this.titleEditControl.setValue(this.service?.task?.description || '');
    this.titleEditControl.markAsPristine();
  }

  protected saveTitleEdit(): void {
    if (!this.titleEditControl.dirty || this.titleEditControl.invalid) return;

    const value = (this.titleEditControl.value || '').trim();
    this.service.task.description = value;
    this.service.form.get(TASK_SIDENAV_FORM.description).setValue(value);
    this.editingTitle = false;
    this.titleEditControl.markAsPristine();
  }

  protected startDescriptionEdit(): void {
    if (!this.allowInlineEditing) return;
    this.editingDescription = true;
    this.contentEditControl.setValue(this.service?.task?.content || null);
    this.contentEditControl.markAsPristine();
  }

  protected cancelDescriptionEdit(): void {
    this.editingDescription = false;
    this.contentEditControl.setValue(this.service?.task?.content || null);
    this.contentEditControl.markAsPristine();
  }

  protected saveDescriptionEdit(): void {
    if (!this.contentEditControl.dirty) return;

    const value = this.contentEditControl.value || null;
    this.service.task.content = value;
    this.service.safeContent = value ? this.sanitizer.bypassSecurityTrustHtml(value) : null;
    this.service.form.get(TASK_SIDENAV_FORM.content).setValue(value);
    this.editingDescription = false;
    this.contentEditControl.markAsPristine();
  }

  protected onEnter(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    this.saveTitleEdit();
  }

  get hasPendingInlineChanges(): boolean {
    return (
      (this.editingTitle && this.titleEditControl.dirty) ||
      (this.editingDescription && this.contentEditControl.dirty)
    );
  }

  discardInlineChanges(): void {
    if (this.editingTitle) {
      this.cancelTitleEdit();
    }

    if (this.editingDescription) {
      this.cancelDescriptionEdit();
    }
  }

  saveInlineChanges(): void {
    const hasTitleChange = this.editingTitle && this.titleEditControl.dirty && !this.titleEditControl.invalid;
    const hasDescriptionChange = this.editingDescription && this.contentEditControl.dirty;

    if (!hasTitleChange && !hasDescriptionChange) {
      return;
    }

    const nextDescription = hasTitleChange
      ? (this.titleEditControl.value || '').trim()
      : this.service?.task?.description || '';
    const nextContent = hasDescriptionChange ? this.contentEditControl.value || null : this.service?.task?.content || null;

    this.service.task.description = nextDescription;
    this.service.task.content = nextContent;
    this.service.safeContent = nextContent ? this.sanitizer.bypassSecurityTrustHtml(nextContent) : null;

    this.service.form.patchValue(
      {
        [TASK_SIDENAV_FORM.description]: nextDescription,
        [TASK_SIDENAV_FORM.content]: nextContent
      },
      { emitEvent: true }
    );

    this.editingTitle = false;
    this.editingDescription = false;
    this.titleEditControl.markAsPristine();
    this.contentEditControl.markAsPristine();
  }

  private syncInlineEditors(): void {
    this.titleEditControl.setValue(this.service?.task?.description || '');
    this.titleEditControl.markAsPristine();
    this.contentEditControl.setValue(this.service?.task?.content || null);
    this.contentEditControl.markAsPristine();
  }

  subscribeTaskUpdate() {
    const sub = this.service.taskActionEmitter.subscribe(() => this.changes.detectChanges());
    this.sub.add(sub);
  }

  openTaskEdit() {
    this.taskManager.editTask(this.service?.task, this.listTaskService.isMyWorkTaskLists);
  }

  openParentPreview(task: Task) {
    this.taskManager.previewTask(task);
  }

  navigateToProject(project?: Project) {
    if (!project) return;

    this.navigateService
      .goWithPromise('project-preview-summary', { projectId: project?.id })
      .then(() => this.service.closeTaskSidenav());
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
